<template>
  <div
    class="w-full h-full relative bg-black flex flex-col"
    :class="{ recording: isRecording, error: isError }"
  >
    <step :step="currentStep" :index="index" />
    <spirit-level v-show="index === 0" :level="orientationLevel" @passed="onSpiritLevelPassed" />
    <div v-if="index > 0" class="tp-wrapper">
      <take-photo ref="photo" @oncanplay="startDetect" @onReady="onReady" @onError="onError" />
      <div v-show="countdown > 0" class="cd-wrapper">
        <p>{{ countdown }}</p>
      </div>
    </div>
    <leave-button @click="abort" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import { wait } from '@/modules/utils';
import ScanProcess from '@/modules/scan-process';
import audioPlayer from '@/mixins/audio-player';
import LeaveButton from '@/components/common/LeaveButton.vue';
import Step from '@/components/MobileScan2/Scan/Step.vue';
import SpiritLevel from '@/components/MobileScan/Scan/SpiritLevel.vue';
import TakePhoto from '@/components/MobileScan2/Scan/TakePhoto.vue';
import PoseDetector from '@/modules/PoseDetector';

// const DEFAULT_USER_HEIGHT = 175;

export default {
  name: 'Scan',
  components: {
    LeaveButton,
    Step,
    SpiritLevel,
    TakePhoto,
  },
  mixins: [audioPlayer],
  data: vm => ({
    index: 0,
    steps: [
      { info: vm.$t('mobileScan.vertical_info') },
      { info: vm.$t('mobileScan2.inscreen_info') },
      { info: vm.$t('mobileScan2.trun_left_info') },
    ],
    orientationLevel: 0,
    isRecording: false,
    countdown: 0,
    isSpiritLevelPassed: false,
    isSpiritLevelKeep: false,
    timer: null,
    isError: false,
  }),
  mounted() {
    if (!ScanProcess.tid) {
      this.abort();
      return;
    }
    if (this.isIOS && !this.isWeb) {
      this.$events.on(HW_EVENTS.SPIRIT_LEVEL, this.onSpiritLevel);
      this.$events.emit(HW_EVENTS.START_SPIRIT_LEVEL_DETECTION);
    } else if (window.DeviceOrientationEvent) {
      this.requestPermission();
      window.addEventListener('deviceorientation', this.onDeviceOrientation);
    }
    this.playAudio('ms-10-scan_vertical', true);
  },
  beforeDestroy() {
    clearTimeout(this.timer);
    if (this.isIOS && !this.isWeb) {
      this.$events.off(HW_EVENTS.SPIRIT_LEVEL, this.onSpiritLevel);
    } else if (window.DeviceOrientationEvent) {
      window.removeEventListener('deviceorientation', this.onDeviceOrientation);
    }
    PoseDetector.close();
  },
  methods: {
    async requestPermission() {
      if (typeof window.DeviceOrientationEvent.requestPermission !== 'function') {
        return;
      }

      try {
        const permission = await window.DeviceOrientationEvent.requestPermission();
        if (permission !== 'granted') {
          this.$toasted.global.alert(this.$t('generic.permisson_device_orientation_not_allowed'));
          this.onSpiritLevel(90);
        }
      } catch (error) {
        console.log(`[ERROR] [requestPermission] ${error.message}`);
        this.$toasted.global.alert(this.$t('generic.permisson_device_orientation_not_support'));
        this.onSpiritLevel(90);
      }
    },
    onDeviceOrientation(event) {
      const { beta } = event;
      if (!Number.isFinite(beta)) return;
      const betaDegree = 180 - Math.round(Math.abs(beta));
      this.onSpiritLevel(betaDegree);
    },
    onSpiritLevel(level) {
      this.orientationLevel = level;
    },
    onSpiritLevelPassed(passed) {
      this.isSpiritLevelPassed = passed;
    },
    startSpiritTimer() {
      this.timer = setTimeout(() => {
        if (this.isSpiritLevelPassed) {
          this.isSpiritLevelKeep = true;
          this.next();
        }
      }, 3000);
    },
    abort() {
      this.$events.goto('HomePage');
    },
    onScanError(error) {
      console.log('onScanError', error);
      this.abort();
    },
    next() {
      const { index } = this;
      if (index === 0) {
        if (this.isIOS && !this.isWeb) {
          this.$events.emit(HW_EVENTS.STOP_SPIRIT_LEVEL_DETECTION);
        }
      }
      if (index === 1) {
        this.startCountDown(3)
          .then(() => {
            this.takePicture(true).then(() => {
              this.index += 1;
            });
          })
          .catch(() => {});
        return;
      }
      if (index === 2) {
        this.startCountDown(3)
          .then(() => {
            this.takePicture(false).then(() => {
              this.index += 1;
              this.next();
            });
          })
          .catch(() => {});
        return;
      }
      const nextIndex = index + 1;
      if (!this.steps[nextIndex]) {
        this.done();
      } else {
        this.index = nextIndex;
      }
    },
    done() {
      ScanProcess.mobileScanResult = {
        frontImage: this.$refs.photo.frontImage,
        sideImage: this.$refs.photo.sideImage,
        frontLandmarks: this.$refs.photo.frontLandmarks,
        sideLandmarks: this.$refs.photo.sideLandmarks,
      };
      this.$events.goto('Analyzing');
    },
    async startDetect(video) {
      PoseDetector.start(video);
    },
    async takePicture(isFront) {
      PoseDetector.pause();
      this.isRecording = true;
      this.playAudio('effect-camera_shutter', true);
      await wait(0.5);
      await this.$refs.photo.takePicture(isFront);
      await wait(0.5);
      this.isRecording = false;
      if (isFront) {
        PoseDetector.resume();
        this.$refs.photo.start();
      }
    },
    startCountDown(countdown) {
      if (this.countdown === -1) return Promise.reject();
      this.countdown = countdown;
      if (countdown === 0) return Promise.resolve();
      // no override: onReady starts ms-19-pose_ready and calls next() straight after, so
      // overriding here would cut that line off on the very first beep
      this.playAudio('effect-countdown');
      return wait(1).then(() =>
        countdown > 0 ? this.startCountDown(countdown - 1) : Promise.resolve()
      );
    },
    stopCountDown() {
      this.countdown = -1;
    },
    onReady(value) {
      if (this.timer) clearTimeout(this.timer);
      if (value) {
        this.timer = setTimeout(() => {
          this.stopAllAudio();
          this.playAudio('ms-19-pose_ready', true);
          this.countdown = 0;
          this.next();
        }, 3000);
      } else {
        this.stopAudio();
        this.stopCountDown();
      }
    },
    onError(value) {
      this.isError = value;
      if (value) {
        this.onReady(false);
      }
    },
  },
  computed: {
    ...mapGetters('user', ['userHeight']),
    ...mapGetters('app', ['isWeb']),
    ...mapGetters('hardware', ['isIOS']),
    currentStep() {
      return this.steps[this.index];
    },
  },
  watch: {
    index(nv) {
      this.$gtag.event(`MobileScan2/Scan/step_${nv}`, {
        event_category: 'scan',
        event_label: nv,
        non_interaction: true,
      });
      if (nv === 1) {
        this.playAudio('ms-11-scan_in_screen', true);
      } else if (nv === 2) {
        this.playAudio('ms-18-turn_left', true);
      }
    },
    isSpiritLevelPassed(nv) {
      if (nv) {
        this.isSpiritLevelKeep = false;
        clearTimeout(this.timer);
        this.startSpiritTimer();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.recording {
  background: var(--brand-gradient);
}
.error {
  background: #ed718d;
}
.tp-wrapper {
  @apply w-full relative flex flex-1;
  padding: 30px 20px;
}
.cd-wrapper {
  @apply flex flex-col absolute justify-center items-center;
  top: 30px;
  bottom: 30px;
  left: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.35);
  h2 {
    font-size: 35px;
    font-weight: 700;
    color: white;
  }
  p {
    font-size: 150px;
    font-weight: 700;
    color: white;
  }
}
</style>
